import React, { memo, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal as BsModal } from 'bootstrap';
import { areEqual } from 'Utils/equalityChecks';

import { NonAppCrewModal } from 'Components/People/PeopleTabs/NonAppCrewTab';

import { useUser } from 'Context/User';
import { UserModel } from 'Containers/User/Models/UserModel';
import { isCompanyAdmin } from 'Utils/roles';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';
import {
  selectedCrewSelector,
  crewCreatedSelector,
  crewUpdatedSelector,
} from '../selectors';
import {
  createCrew,
  updateCrew,
  setCrewCreated,
  setCrewUpdated,
  setSelectedCrew,
} from '../actions';

const MODAL_ID = 'non-app-crew-modal';

interface Props {
  isOpen: boolean;
  onClose: (e?: any) => void;
  onSaved?: () => void;
  onDeactivate?: (e: any) => void;
}

const NonAppCrewModalContainer = ({ isOpen, onClose, onSaved, onDeactivate }: Props) => {
  const dispatch = useDispatch();
  const user: UserModel = useUser();

  const firstCompanyId = useSelector(firstCompanyIdSelector, areEqual);
  const selectedCrew = useSelector(selectedCrewSelector, areEqual);
  const crewCreated = useSelector(crewCreatedSelector, areEqual);
  const crewUpdated = useSelector(crewUpdatedSelector, areEqual);

  const isEditMode = Boolean(selectedCrew?.id);
  const [isAdmin] = useState(isCompanyAdmin(user.roles));

  // Close via bootstrap's own hide() (the reliable path the header "X" uses),
  // then run the parent's state reset. Relying on the shared Modal's isOpen
  // effect alone did not dismiss the dialog.
  const handleClose = useCallback(
    (e?: any) => {
      const el = document.getElementById(MODAL_ID);
      const instance = el ? BsModal.getInstance(el) : null;
      if (instance) instance.hide();
      if (onClose) onClose(e);
    },
    [onClose]
  );

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [phoneType, setPhoneType] = useState('mobile');
  const [laborRate, setLaborRate] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (selectedCrew?.id) {
        setFirstName(selectedCrew.firstName || '');
        setLastName(selectedCrew.lastName || '');
        setContactEmail(selectedCrew.email || '');
        setPhoneValue(selectedCrew.primaryPhone || '');
        setPhoneType(selectedCrew.phone?.type || 'mobile');
        setLaborRate(selectedCrew.laborRate != null ? String(selectedCrew.laborRate) : '');
      } else {
        setFirstName('');
        setLastName('');
        setContactEmail('');
        setPhoneValue('');
        setPhoneType('mobile');
        setLaborRate('');
      }
    }
  }, [isOpen, selectedCrew]);

  useEffect(() => {
    if ((crewCreated || crewUpdated) && isOpen) {
      dispatch(setCrewCreated(false));
      dispatch(setCrewUpdated(false));
      dispatch(setSelectedCrew(null));
      handleClose();
      if (onSaved) onSaved();
    }
  }, [crewCreated, crewUpdated, isOpen]);

  const handleSave = useCallback(() => {
    if (!firstName.trim()) return;

    const requestData: any = {
      first_name: firstName.trim(),
    };

    if (lastName.trim()) {
      requestData.last_name = lastName.trim();
    }

    if (contactEmail.trim()) {
      requestData.contact_email = contactEmail.trim();
    }

    if (phoneValue.trim()) {
      requestData.phone = {
        value: phoneValue.trim(),
        type: phoneType,
      };
    }

    if (laborRate.trim()) {
      requestData.labor_rate = parseFloat(laborRate);
    } else if (laborRate.trim() === '' && isEditMode) {
      requestData.labor_rate = null;
    }

    if (isEditMode && selectedCrew?.id) {
      dispatch(updateCrew(firstCompanyId, selectedCrew.id, requestData));
    } else {
      dispatch(createCrew(firstCompanyId, requestData));
    }
  }, [
    firstCompanyId,
    firstName,
    lastName,
    contactEmail,
    phoneValue,
    phoneType,
    laborRate,
    isEditMode,
    selectedCrew,
  ]);

  return (
    <NonAppCrewModal
      isOpen={isOpen}
      isEditMode={isEditMode}
      firstName={firstName}
      lastName={lastName}
      contactEmail={contactEmail}
      phoneValue={phoneValue}
      phoneType={phoneType}
      laborRate={laborRate}
      canViewCost={isAdmin}
      onClose={handleClose}
      onFirstNameChange={(e) => setFirstName(e.target.value)}
      onLastNameChange={(e) => setLastName(e.target.value)}
      onContactEmailChange={(e) => setContactEmail(e.target.value)}
      onPhoneValueChange={(e) => setPhoneValue(e.target.value)}
      onPhoneTypeChange={(e) => setPhoneType(e.target.value)}
      onLaborRateChange={(e) => setLaborRate(e.target.value)}
      onSave={handleSave}
      onDeactivate={onDeactivate}
    />
  );
};

const NonAppCrewModalContainerMemo = memo(NonAppCrewModalContainer, areEqual);

export { NonAppCrewModalContainerMemo as NonAppCrewModal };
